export const Capabilities = {
  FIAT_PAYOUTS_B2C: { group: 'FIAT', name: 'PAYOUTS_B2C' },
} as const

export const CapabilitiesScope = {
  GLOBAL: 'GLOBAL',
  ACCOUNT: 'ACCOUNT',
  MERCHANT: 'MERCHANT',
  PARTNER: 'PARTNER',
  PARTNER_ACCOUNT: 'PARTNER_ACCOUNT',
  PROCESSING_PROFILE: 'PROCESSING_PROFILE',
  WALLET: 'WALLET',
} as const

export type CapabilitiesScope = (typeof CapabilitiesScope)[keyof typeof CapabilitiesScope]

export const CapabilitiesFormat = {
  BOOLEAN: 'BOOLEAN',
  NUMBER: 'NUMBER',
  STRING: 'STRING',
} as const

export type CapabilitiesFormat = (typeof CapabilitiesFormat)[keyof typeof CapabilitiesFormat]

export interface CapabilitiesRequest {
  group?: string
  name?: string
  scope?: CapabilitiesScope
  subjectId?: string
}

export interface CapabilitiesResponse {
  group: string
  name: string
  scope: CapabilitiesScope
  format: CapabilitiesFormat
  subjectId: string
  value: boolean | number | string
}

export interface BulkCapabilityRequest {
  capabilities: BulkCapabilityRequestFilter[]
}

export interface BulkCapabilityRequestFilter {
  group: string
  name: string
  objects: BulkCapabilityRequestFilterObject[]
}

export interface BulkCapabilityRequestFilterObject {
  scope: CapabilitiesScope
  subjectId: string
}

export interface BulkCapabilityResponse {
  FIAT: {
    // Wallet capabilities
    PAYOUTS_FIRST_PARTY: { WALLET: CapabilitySubjectValue }
    PAYOUTS_THIRD_PARTY: { WALLET: CapabilitySubjectValue }
    WALLET_ACTIONS: { WALLET: CapabilitySubjectValue }
    PAYOUTS_B2C: { WALLET: CapabilitySubjectValue }
    PAYINS: { WALLET: CapabilitySubjectValue }
    // Account capabilities
    BENEFICIARIES_CUBIX: { ACCOUNT: CapabilitySubjectValue }
    BENEFICIARIES_ACH_FEDWIRE: { ACCOUNT: CapabilitySubjectValue }
    BENEFICIARIES_AED: { ACCOUNT: CapabilitySubjectValue }
    BENEFICIARIES_CRNOW: { ACCOUNT: CapabilitySubjectValue }
    BENEFICIARIES_RTP: { ACCOUNT: CapabilitySubjectValue }
    BENEFICIARIES_FEDNOW: { ACCOUNT: CapabilitySubjectValue }
    BENEFICIARIES_LATAM: { ACCOUNT: CapabilitySubjectValue }
    BENEFICIARIES_MC_MOVE: { ACCOUNT: CapabilitySubjectValue }
  }
  CRYPTO: {
    US_STATES_CRYPTO_PAXOS_ENABLED: { ACCOUNT: CapabilitySubjectValue }
  }
  // Account capabilities
  ACCOUNT: {
    PARTNER_MANAGED_CUSTOMER_COMPANY: { ACCOUNT: CapabilitySubjectValue }
    EARNING_REWARDS: { ACCOUNT: CapabilitySubjectValue }
    PARTNER_MANAGED_CUSTOMER_INDIVIDUAL: { ACCOUNT: CapabilitySubjectValue }
    PRINCIPLE_CUSTOMER_COMPANY: { ACCOUNT: CapabilitySubjectValue }
    PRINCIPLE_CUSTOMER_INDIVIDUAL: { ACCOUNT: CapabilitySubjectValue }
    EMBEDDED_CUSTOMER_INDIVIDUAL: { ACCOUNT: CapabilitySubjectValue }
    EMBEDDED_CUSTOMER_COMPANY: { ACCOUNT: CapabilitySubjectValue }
  }
  NETWORK: { PARTICIPANT: { ACCOUNT: CapabilitySubjectValue } }
  EPC: {
    WALLET_ACTIONS: { ACCOUNT: CapabilitySubjectValue }
    EMBEDDED_WALLET_CREATION: { ACCOUNT: CapabilitySubjectValue }
  }
}

export interface CapabilitySubjectValue {
  value: string
  error: boolean
}

export interface AccountCapabilities {
  isPartnerManagedCustomerCompanyCapabilityEnabled: boolean
  isPartnerManagedCustomerIndividualCapabilityEnabled: boolean
  // Underlying customer (nested and embedded)
  isPrincipleCustomerCompanyCapabilityEnabled: boolean
  isPrincipleCustomerIndividualCapabilityEnabled: boolean
  isEmbeddedCustomerCompanyCapabilityEnabled: boolean
  isEmbeddedCustomerIndividualCapabilityEnabled: boolean
  isEarningRewardsCapabilityEnabled: boolean
  // Crypto capabilities
  paxosUsStatesCapability: string[]
  // Fiat beneficiaries
  isCubixBeneficiaryCapabilityEnabled: boolean
  isAedBeneficiaryEnabled: boolean
  isUsdCRNowBeneficiaryCapabilityEnabled: boolean
  isUsdRtpBeneficiaryCapabilityEnabled: boolean
  isUsdFedNowBeneficiaryCapabilityEnabled: boolean
  isLatamBankAccountCapabilityEnabled: boolean
  isMcMoveBeneficiaryCapabilityEnabled: boolean
  // EPC wallet actions
  isEpcWalletPayinsCapabilityEnabled: boolean
  isEpcWalletPayoutsCapabilityEnabled: boolean
  isEpcWalletInternalTransfersCapabilityEnabled: boolean
  isEpcEmbeddedCustomerWalletCreationCapabilityEnabled: boolean
}
